import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useState } from "react";
import { useRemoveWorkTeamMemberMutation } from "work/work-agreements/_api/work-agreement-api";
import { WorkTeamSide } from "work/work-agreements/teams/leader-handover/team-leader-handover";
import { TEAM_NAMES, type RemoveFromWork } from "work/work-agreements/teams/_views/remove-from-work-row-actions";
import { ratesStruckThroughWarning } from "work/work-agreements/teams/_views/rates-struck-through-warning";
import WorkAgreement from "work/work-agreements/work-agreement";

/**
 * The client team leader's removal of a member from active work, for each roster on the page, or
 * nothing for a roster the reader may not remove from.
 *
 * Only the client team leader, acting for the client's organization, may remove anybody, and only from
 * work that is running -- which is what `POST work/{id}/teams/{side}/members/{userId}/remove` asserts.
 * A team leader is never offered: they leave by handing the team over.
 */
export function useRemoveFromWork(
  workAgreement: WorkAgreement | null | undefined,
  isClientTeamLeader: boolean,
  refreshAfterWriteConflict: (error: unknown) => Promise<boolean>,
): Partial<Record<WorkTeamSide, RemoveFromWork>> {
  const confirm = useConfirmDialog();
  const [removeWorkTeamMember] = useRemoveWorkTeamMemberMutation();
  const [busy, setBusy] = useState(false);

  if (!workAgreement?.id || workAgreement.version === undefined || !isClientTeamLeader || !workAgreement.isActiveWork) {
    return {};
  }
  const workId = workAgreement.id.value;
  const version = workAgreement.version;
  const sourceProposalId = workAgreement.sourceProposalId?.value;

  async function remove(side: WorkTeamSide, userId: Guid, displayName: string) {
    const rateWarning = side === WorkTeamSide.Vendor ? ratesStruckThroughWarning(workAgreement!, userId) : "";
    const response = await confirm({
      title: "Remove from work?",
      message:
        `${displayName} will be removed from the ${TEAM_NAMES[side]} and lose access to this work. ` +
        `The work continues without them, and everyone on the work will be told.${rateWarning}`,
      okButtonText: "Remove",
    });
    if (response === ConfirmResponse.Cancel) return;

    setBusy(true);
    const outcome = await removeWorkTeamMember({ workId, version, side, userId: userId.value, sourceProposalId })
      .unwrap()
      .then(
        () => undefined,
        (error: unknown) => error,
      );
    setBusy(false);
    if (outcome === undefined) {
      enqueueSnackbar(`${displayName} was removed from the work`, { variant: "success" });
      return;
    }
    console.error(outcome);
    if (await refreshAfterWriteConflict(outcome)) return;
    enqueueSnackbar(getErrorMessageFromUnknown(outcome) ?? `Failed to remove ${displayName}`, { variant: "error" });
  }

  return {
    [WorkTeamSide.Client]: {
      side: WorkTeamSide.Client,
      busy,
      remove: (userId, displayName) => void remove(WorkTeamSide.Client, userId, displayName),
    },
    [WorkTeamSide.Vendor]: {
      side: WorkTeamSide.Vendor,
      busy,
      remove: (userId, displayName) => void remove(WorkTeamSide.Vendor, userId, displayName),
    },
  };
}
